export const PAGE_HELP = {
  overview: {
    title: 'Tender Management',
    summary: 'The home of Tender Management. It is the application view of the book of work. Each module keeps its own records.',
    who: [
      'Anyone who can open Tender Management reads this page.',
      'A bid manager or the tender administrator starts a pursuit with New tender.',
      'Reviewers and approvers open Approvals for a decision. They do not decide from this home.'
    ],
    edit: [
      'Nothing on this page is edited. The tiles and the month switch are a layout preview. They are not the live register.',
      'Open the register, or New tender, when a pursuit needs a change.'
    ],
    use: [
      'Read the tiles as a preview of identified, active, due, and submitted work.',
      'Use the calendar for this month. A marked day is a deadline already on a tender.',
      'Open a row in the side list to jump to the register or the approval queue.',
      'Switch the month control between this month and this quarter to change the sample counts.',
      'New tender starts a pursuit in the register. The journey, the fee, the team packages, and the submission are kept on that tender.'
    ],
    fields: [
      ['Identified', 'Pursuits captured and not yet in preparation. This tile is a preview.'],
      ['Active bids', 'Pursuits the team is assessing or preparing. This tile is a preview.'],
      ['Open pipeline', 'A preview of the estimated value of pursuits that are still open.'],
      ['Due this week', 'A preview of pursuits that need a decision or a submission this week.'],
      ['Submitted', 'A preview of bids already sent and waiting on a result.'],
      ['Win rate', 'A preview of awarded pursuits as a share of closed pursuits over the trailing 90 days.']
    ],
    links: [
      'The register is the list of pursuits. Open a row for the journey, work packages, fee and EMD, and submission.',
      'Approvals holds go / no-go, technical sections, document versions, commercial estimates, and timesheets.',
      'BG / EMD is the instrument register. Cash outstanding and guarantee exposure stay in separate totals.',
      'Reports reads the same records. The on-screen filter is what Export Excel writes.',
      'Clients, services, and sectors come from Masters.'
    ],
    ai: {
      helps: 'This page does not call the provider. AI sits on an open tender and on reusable content in the knowledge bank.',
      flow: ['Open a pursuit', 'Documents', 'Technical response', 'Eligibility', 'Document Q&A'],
      use: [
        'Open the tender from the register. Help on that page lists Draft with AI, Suggest notes, Summarise, and Ask.',
        'AI stays quiet until an administrator saves a Gemini or OpenAI key under Settings and turns Use AI on.'
      ]
    }
  },
  register: {
    title: 'Tender register',
    summary: 'The list of every pursuit. Capture a tender here, then open it to run the journey, the packages, the fee, and the submission.',
    who: [
      'The bid manager captures a pursuit and keeps it up to date while it is in progress.',
      'The tender administrator can do the same.',
      'A reviewer or an approver opens the row to read it. They decide in the approval centre, not by editing the register row.'
    ],
    edit: [
      'Open the row menu. View is always there.',
      'Edit is there while the pursuit is still open, including Eligibility, Bid decision, In preparation, Ready to submit, Under evaluation, and Result declared.',
      'Submitted and a closed outcome are view only. That covers Won, Lost, No-bid, Not eligible, Not qualified, Withdrawn, and Cancelled once the tender is closed.'
    ],
    use: [
      'New tender opens a blank record. Save it and the pursuit appears in this list.',
      'Search matches the reference, title, and service.',
      'Filter narrows the list by the short choice columns, such as stage. Clear puts every choice back to All.',
      'Open the row menu and choose View to read the record. Edit is offered only while the pursuit is still in progress.',
      'Submitted and closed outcomes stay on the list and open as view only.'
    ],
    fields: [
      ['Reference', 'The firm’s number for the pursuit, such as DCSL-2026-008.'],
      ['Opportunity', 'The tender title. The client name sits on the line underneath.'],
      ['Service', 'The service Dhruv is offering, taken from the service master.'],
      ['Stage', 'The journey or the outcome: Identified, Eligibility, Bid decision, In preparation, Ready to submit, Submitted, Under evaluation, Result declared, or a closed outcome such as Won, Lost, No-bid, Not eligible, Not qualified, Withdrawn, or Cancelled.'],
      ['Deadline', 'The date the bid must reach the client. A past date on an open pursuit is shown as overdue.'],
      ['Client', 'On the record, the authority or private client from the customer master.'],
      ['Tender type', 'The form of the bid, such as Government RFP or a limited tender, from the tender type master.'],
      ['Sector', 'The sector being pursued, from the sector master.'],
      ['Source', 'Where the opportunity was found, from the source master.']
    ],
    links: [
      'Client, service, sector, tender type, and source are chosen from the active rows in Masters. An inactive master row stays off those lists.',
      'An authorised Go moves the tender to In preparation and creates the four work packages. No-bid closes it and does not create those packages.',
      'Project experience in the knowledge bank and the people on Personnel are what the technical response draws on.',
      'Rates feed the commercial estimate. After award, effort is logged on Timesheets against this tender.'
    ],
    ai: {
      helps: 'The register itself does not draft or answer. Open a row and the tender page holds the AI screens.',
      flow: ['Open the tender', 'Prepare the file', 'Ask, draft, or suggest', 'Save what you will keep'],
      use: [
        'Documents: set classification to Standard, tick Eligible, then Extract text and Summarise.',
        'Document Q&A: tick the files, start a conversation, and ask. The answer names its sources.',
        'Technical response: Draft with AI fills the section. You still save it and a reviewer still approves it.',
        'Eligibility: Suggest notes fills gap notes and mitigation. Go / no-go stays a human decision.'
      ],
      note: 'A commercial confidential or restricted file is not sent to the provider. Eligible can be ticked only on a Standard file.'
    }
  },
  knowledge: {
    title: 'Knowledge bank',
    summary: 'The firm’s bid library. You add certificates, past projects, and reusable text here. People preparing a tender read this list. They do not change it.',
    who: [
      'The bid manager keeps the library current: certificates, insurance, registrations, and completed projects. They also attach each credential to the active tenders that will cite it.',
      'The tender administrator can add the same records and attach them to open pursuits.',
      'A technical reviewer or a contributor adds reusable text for the response. They do not maintain the certificates.',
      'Everyone else who can open the page is here to read. Check a credential before you claim it on a bid. Do not add the firm’s certificates unless that is your job.'
    ],
    use: [
      'The page is a register with three lists: Credentials, Project experience, and Reusable content.',
      'New credential, New project, or New content opens a full page for that record.',
      'Open the row menu. View reads the record. Edit updates it.',
      'On a credential, save it first. Then attach it to each open pursuit that will cite it.',
      'A project records an assignment, completed or ongoing, with client, sector, and service from Masters. Civil project value and the consultancy fee are separate, and a blank amount stays blank.',
      'Reusable content is a draft paragraph until its status is set to approved.'
    ],
    edit: [
      'Edit opens the record on its own page. Change the fields, then Save changes.',
      'A renewed certificate can be a new credential with the new Valid until date. The earlier row stays in the list.',
      'Remove on the credential page takes it off one pursuit. The library record stays.',
      'Submitted, awarded, and lost tenders are not offered when you attach a credential.'
    ],
    fields: [
      ['Credential title', 'The certificate, registration, or policy, such as ISO 9001.'],
      ['Category', 'Certification, registration, insurance, accreditation, or other.'],
      ['Issuer', 'The body that issued the credential.'],
      ['Valid to', 'The expiry date. The list marks a credential as valid, expiring soon, or expired.'],
      ['On tenders', 'How many open pursuits currently cite this credential.'],
      ['Project name', 'A past assignment you may cite.'],
      ['Client', 'The authority or client on that project, chosen from the customer master.'],
      ['Sector and service', 'The sector and the service on that project, chosen from those masters.'],
      ['Reusable title and content', 'Approved narrative you can pin into a technical section.']
    ],
    links: [
      'Client, sector, and service on a project use the same masters as the tender register.',
      'Attaching a credential records that an active tender will cite it. Open that tender to see the same credentials.',
      'Eligibility reviews on a pursuit look here for proof the firm has done the work before.',
      'Personnel holds the people named in a technical response. This page holds the firm’s credentials and past work.'
    ],
    ai: {
      helps: 'On a reusable-content record, Suggest wording drafts the paragraph so the writer starts from a full text instead of a blank box. Credentials and project experience are not drafted.',
      flow: ['Enter the title', 'Suggest wording', 'Read the paragraph', 'Save as draft', 'Approve when it can be pinned'],
      use: [
        'Open Reusable content and choose New content, or edit an existing row.',
        'Enter a title. Suggest wording needs a title. Any text already in the box is taken into account.',
        'The suggestion replaces the content box. Change it before you save.',
        'The record stays a draft until status is set to approved. Only an approved paragraph is included when a technical section is drafted.'
      ],
      note: 'If the page says AI is not configured, an administrator turns it on under Settings, then AI.'
    }
  },
  personnel: {
    title: 'Personnel',
    summary: 'The firm roster of employees and consultants. Adding a person here does not put them on a tender. Naming them on a pursuit pins their CV. A named person is not an approved delivery team until that team version is approved by someone else.',
    who: [
      'The bid manager or the tender administrator keeps the roster and names people on active tenders.',
      'The person writing the technical response reads who is already named. They do not add employees.'
    ],
    edit: [
      'Edit opens the person on their own page. Change the name, designation, type, CV version, sectors, or status, then Save changes.',
      'Remove on that page takes the person off one pursuit. They stay on the roster.',
      'Mark the person inactive when they should no longer be named on new tenders.'
    ],
    use: [
      'The list is the roster. New person opens a full page for the profile.',
      'Open the row menu. View reads the person. Edit updates the profile and names them on tenders.',
      'On the person page, save the profile first. Then choose the role and the open pursuits, and name them on those tenders.',
      'The On tenders column counts how many pursuits currently name them.',
      'Submitted, awarded, and lost tenders are not offered when you name someone.'
    ],
    fields: [
      ['Name', 'The person as they should appear on a proposal.'],
      ['Designation', 'The role title, such as Resident Engineer.'],
      ['Type', 'Employee of the firm, or a consultant engaged for bids.'],
      ['CV version', 'The résumé version the bid team should attach.'],
      ['Sectors', 'The sectors this person can be proposed for, chosen from the sector master. More than one can be selected.'],
      ['Experience summary', 'A short note for the people assembling the technical response.'],
      ['Status', 'Active people can be named on a tender. Inactive people stay on the roster and drop out of the attach list.'],
      ['On tenders', 'How many open pursuits currently name this person.']
    ],
    links: [
      'This page is the employee and consultant list. It stays its own module. Masters holds the short lists a tender dropdown uses: customers, services, sectors, tender types, and sources.',
      'Sectors on a person come from the sector master.',
      'Open a tender and use Team / Resources to record a role slot, the person named on it, and a human assessment of the stated qualification and experience. An authorised resource user submits the team. A different person with management-approval permission approves or returns it. Final rate approval is not in this release.',
      'Timesheets record the hours once work is under way.'
    ]
  },
  timesheets: {
    title: 'Timesheets',
    summary: 'Effort booked to a tender or an awarded assignment, then submitted for approval.',
    who: [
      'Each person logs their own time and submits it.',
      'A timesheet approver approves or returns someone else’s submitted entry. You cannot approve your own.'
    ],
    edit: [
      'Edit opens your draft or returned entry on its own page. Change the hours or the note, then submit it.',
      'A submitted entry waits for the approver. It stays view only until they return it.',
      'An approved entry is locked.'
    ],
    use: [
      'The list shows every timesheet the firm has logged. Only my entries narrows it to your own.',
      'New entry opens a full page. Save the day as a draft, then submit it for approval.',
      'Open the row menu. View reads the entry. Edit is there for your own draft or a returned entry.',
      'A timesheet approver opens a submitted entry that is not their own and approves it, or returns it with a note.'
    ],
    fields: [
      ['Work against', 'Tender or Assignment. Internal time is not part of this module.'],
      ['Activity', 'Taken from the Timesheet activities master and filtered by the work type. Inactive activities stay on old entries only.'],
      ['Hours', 'Positive time in the increment set under Timesheet rules. The daily total cannot pass the configured limit.'],
      ['Assigned approver', 'Resolved from the assignment or tender owner, then another timesheet approver. Shown read-only.'],
      ['Status', 'Draft, submitted, returned, or approved. Approved entries stay locked unless an authorised person reopens them with a reason.']
    ],
    links: [
      'The tender list is the register. Awarded rows are pursuits that have reached Awarded.',
      'Submitted entries wait in the approval centre for a decision.',
      'Approved commercial estimates use the rate master. Timesheets record the hours those rates apply to.'
    ]
  },
  approvals: {
    title: 'Approvals',
    summary: 'The register of decisions waiting on the firm: go / no-go, technical and commercial sign-off, document versions, and timesheet approval. The submission pack is approved on the tender, not from this list.',
    who: [
      'A platform administrator or tender administrator can decide any request that is not their own.',
      'A bid approver decides go / no-go. A technical reviewer decides the response and document versions. A commercial reviewer decides the estimate. A timesheet approver decides submitted time.',
      'The person who prepared the request does not approve their own record.'
    ],
    edit: [
      'Decide opens the request on its own page. Approve accepts it. Return sends it back, and a note is required.',
      'View reads the request without changing it.',
      'You do not edit the tender, the estimate, or the timesheet from this list. The decision updates that record.'
    ],
    use: [
      'The list shows requests still waiting. Include decided adds ones that have already been approved or returned.',
      'Open the row menu. View reads the request. Decide is there when your role can act on it.',
      'On the request page, read what will change, then approve it or return it with a note.',
      'Search finds a request by its title, type, or tender.'
    ],
    fields: [
      ['Request', 'What is waiting: a go / no-go, a technical section, a document version, a commercial estimate, or a timesheet.'],
      ['Type', 'The kind of decision.'],
      ['Tender', 'The pursuit the request belongs to.'],
      ['Raised by', 'Who submitted the underlying record.'],
      ['Due', 'When the decision is expected.'],
      ['Status', 'Pending, overdue, approved, or returned.']
    ],
    links: [
      'A go / no-go here records the bid decision for that tender. The person who submitted the score cannot approve it.',
      'Management approval of the submission pack is on the tender’s Submission tab. The person who prepares the pack cannot approve that version. The tender owner then records the portal submission. Recording the acknowledgement does not approve the pack.',
      'Timesheet approvals clear entries submitted from Timesheets.',
      'Commercial approvals sit on estimates that use the rate master.',
      'A returned timesheet goes back to the person who logged it so they can correct and submit it again.'
    ]
  },
  reports: {
    title: 'Reports',
    summary: 'Live management reports for pursuit, work packages, fee and EMD, approvals, commercials, and delivery.',
    use: [
      'Open any card to load current figures from the live register and related records.',
      'Use the stage bar, the chips, the date window, the value bands, or search to narrow the rows. The figures on the cards stay the full report. The table and Export Excel follow the criteria. Export Excel writes a .xlsx workbook.',
      'Click a column heading to sort. Use Refresh to pull the latest snapshot.',
      'Click a tender row where offered to open that pursuit in the register.'
    ],
    fields: [
      ['Pipeline overview', 'Pursuits by stage and value, from capture to close.'],
      ['Win / loss and Go / no-go', 'Awarded and lost outcomes, and decisions that stopped a pursuit.'],
      ['Deadline watch', 'Submission and clarification dates that are due or overdue.'],
      ['Pending approvals', 'Technical, commercial, and bid approvals still waiting.'],
      ['Credential expiry', 'Registrations and certificates that need renewal before a bid.'],
      ['Effort and timesheets', 'Planned hours against submitted, approved, and returned time.'],
      ['Work packages', 'The four packages on each pursuit: owner, due date, status, blocker, and overdue age. It does not show rates or amounts.'],
      ['Fee, EMD and BG', 'Tender fee, cash EMD, cash held as performance security, guarantee exposure, and refund age. Cash outstanding, cash held, and guarantee exposure are separate totals. A legacy instrument with no stored settlement stays Unclassified and is in neither total. A performance security with no stored settlement is a guarantee.'],
      ['Commercial summary', 'Cost estimates and rates for pursuits in preparation. Rate amounts stay on the reports that already require costing access.'],
      ['Client and sector mix', 'Pipeline value by client, sector, and service.']
    ],
    links: [
      'Figures are drawn from the tender register, the approval centre, the rate master, and timesheets.',
      'Client, sector, and service breakdowns use the masters chosen on each tender.',
      'The overview on the home page is the day-to-day view of the same pipeline.'
    ]
  },
  rates: {
    title: 'Rate master',
    summary: 'Confidential rates by designation. A master rate is a starting value. It is not a Final tender rate. An approved estimate keeps the copy it used.',
    who: [
      'The tender administrator or the bid manager maintains these rates.',
      'A commercial reviewer reads them when checking an estimate. They do not change the master from the estimate.'
    ],
    edit: [
      'A new rate is a new row, with its own Effective from date. You do not overwrite the earlier rate.',
      'Deactivate a row when it should stop being offered. An approved estimate keeps the rate it already used.'
    ],
    use: [
      'Open Costing Rates from Masters. New opens a blank rate. Choose internal effort cost or charge-out before saving.',
      'View reads the rate. Edit changes the designation, the amount, the basis, the start date, or the status.',
      'Set status to inactive when the firm should stop offering that rate. An approved estimate keeps the amount it already used.'
    ],
    fields: [
      ['Purpose', 'Internal effort cost is used for approved hours. Charge-out is used for proposed client pricing. A rate without a purpose is flagged and is not used.'],
      ['Designation', 'The job title the rate applies to. It is chosen from Designations and is not an application access role.'],
      ['Amount', 'The INR rate for that basis.'],
      ['Basis', 'Hourly or monthly.'],
      ['Effective from', 'The first day this rate should be used.']
    ],
    links: [
      'A commercial estimate uses a charge-out rate. A timesheet uses an internal effort-cost rate. The two are not interchangeable.',
      'An active rate does not finish the Costing work package. Costing can be marked in progress or blocked. Ready for review and Done stay closed until a Final rate approval exists. That approval is not in this release.',
      'Once an estimate is approved, it keeps the rate snapshot it used, even if this master changes later.',
      'A rate that still says Needs classification is not used. Choose its purpose before a calculation can include it.'
    ]
  },
  masters: {
    title: 'Masters',
    summary: 'Clients and authorities, services, sectors, tender types, sources, document types, designations, cost heads, instrument types, due-diligence checklists, outcome reasons, and costing rates.',
    who: [
      'The tender administrator and the platform administrator maintain these lists.',
      'A bid manager uses the active names when capturing a tender. They do not add customers or services from the tender form.'
    ],
    edit: [
      'Open the list, then use the row menu. View reads the record. Edit changes it. Delete asks you to confirm.',
      'Edit is needed when a name, code, or status is wrong. Mark the row inactive when the firm should stop offering it. Leave it in place so older tenders still show the name they used.'
    ],
    use: [
      'Open a card to see that list. Upload Center downloads an Excel template for each master and adds the filled rows.',
      'New adds a record. The number, such as DCSL-C-001 or DCSL-S-001, is assigned when you save if you leave it blank.',
      'View reads the record. Edit changes it. Delete asks you to confirm, then removes it from the list.',
      'Mark a row inactive when the firm should stop offering it. Inactive rows stay in the master and drop out of the tender lists.'
    ],
    fields: [
      ['Clients / Authorities', 'The authority or private client. The tender’s Client field uses the active names. The stored route is still the customer list.'],
      ['Document types', 'The name of an uploaded file. The type does not grant access and does not make the file eligible for AI.'],
      ['Designations', 'Job titles for personnel and costing rates. A designation is not the role on one tender and it is not an application access role.'],
      ['Cost heads', 'Lines on a commercial estimate. Overhead heads are not also multiplied by the estimate overhead percent.'],
      ['Security / instrument types', 'EMD, bid security, performance BG, and other. Issued, extended, and released stay controlled states.'],
      ['Due-diligence checklists', 'A partner review copies the template version it starts from. Later edits do not change that review.'],
      ['Outcome reasons', 'Why a tender was lost, withdrawn, cancelled, or marked No-Go. Other requires remarks.'],
      ['Services', 'What Dhruv offers, such as DPR or PMC, with category and billing basis.'],
      ['Sectors', 'Roads, bridges, metro, water, and the other sectors you pursue.'],
      ['Tender types', 'The form of the bid: Government RFP, public procurement, an EOI, a limited tender, and the rest.'],
      ['Sources', 'Where the opportunity was found: a portal, an authority site, or a direct invitation.'],
      ['Costing Rates', 'An internal effort-cost rate or a charge-out rate, hourly or monthly, from a start date.'],
      ['Code and status', 'The short code shown beside the name in a tender list. Status is active or inactive.']
    ],
    links: [
      'The tender record’s client, service, sector, tender type, and source are searchable lists of the active rows here.',
      'Project experience in the knowledge bank uses the customer, sector, and service lists.',
      'Personnel uses the sector list for the sectors a person can be proposed for.',
      'Changing a name here changes what users see the next time they open those lists.'
    ]
  },
  customers: {
    title: 'Clients / Authorities',
    summary: 'Authorities and private clients Dhruv bids for. The Client field on a tender uses the active names from this list.',
    use: [
      'New opens a blank customer. Save it and the row appears here.',
      'Search finds a customer by number, name, or city.',
      'View reads the record. Edit changes it. Delete asks you to confirm first.',
      'Set status to inactive when the firm should stop selecting that client. The row stays here and drops out of the tender list.'
    ],
    fields: [
      ['Cust no.', 'The firm’s number for the customer, such as DCSL-C-001. It is assigned on save when left blank.'],
      ['Category', 'Chosen from the active client categories under Settings → Organisation, in the order defined there.'],
      ['Name', 'The name shown as Client on a tender and on project experience.'],
      ['PAN', 'The client’s PAN, when you hold it. Leave it blank if you do not.'],
      ['Currency', 'The currency this client usually contracts in. A tender can still set its own currency.'],
      ['Location', 'The city or office you deal with.'],
      ['Status', 'Active customers can be chosen on a tender. Inactive customers cannot.'],
      ['Portal URL', 'The authority’s procurement portal, separate from the public website.'],
      ['Refund follow-up', 'Days after a security expires before a refund reminder. Blank keeps the pursuit rule. An instrument can set its own number.']
    ],
    links: [
      'The tender register’s Client field is a searchable list of active customers.',
      'Project experience in the knowledge bank uses the same list for the client on a past project.',
      'Client mix on Reports groups pipeline value by these names.'
    ]
  },
  services: {
    title: 'Services',
    summary: 'The services Dhruv offers. The Service field on a tender uses the active names from this list.',
    use: [
      'New opens a blank service. The number is assigned on save when left blank.',
      'Search finds a service by number, code, or name.',
      'View, Edit, and Delete work the same way as on the other master lists.',
      'Mark a service inactive when it should no longer be offered on a new tender.'
    ],
    fields: [
      ['Service no.', 'The firm’s number, such as DCSL-S-001.'],
      ['Code', 'The short code shown beside the name, such as DPR or PMC.'],
      ['Name', 'The service as it appears on a tender and in the register.'],
      ['Category', 'Chosen from the active service categories under Settings → Organisation, in the order defined there.'],
      ['Billing', 'Chosen from the active billing bases under Settings → Organisation, in the order defined there.'],
      ['Status', 'Only active services appear in the tender’s Service list.']
    ],
    links: [
      'The tender form and the register’s Service column use this list.',
      'Project experience in the knowledge bank records the service delivered on a past job.',
      'Reports that split the pipeline by service line use these names.'
    ]
  },
  sectors: {
    title: 'Sectors',
    summary: 'The sectors Dhruv pursues. A tender and a person’s sector experience both choose from the active rows here.',
    use: [
      'New adds a sector. The number is assigned on save when left blank.',
      'Search finds a sector by number, code, or name.',
      'Keep the group consistent so roads, structures, water, and rail stay easy to scan.',
      'Mark a sector inactive when the firm is no longer pursuing it.'
    ],
    fields: [
      ['Sector no.', 'The firm’s number, such as DCSL-SEC-001.'],
      ['Code', 'The short code, such as HWY or MET.'],
      ['Name', 'The sector name stored on the tender, such as Roads and Highways.'],
      ['Group', 'Chosen from the active sector groups under Settings → Organisation, in the order defined there.'],
      ['Status', 'Active sectors can be chosen on a tender and on a person. Inactive sectors cannot.']
    ],
    links: [
      'The tender form’s Sector field is a searchable list of active sectors.',
      'Personnel lets you tag a person with one or more of these sectors.',
      'Project experience in the knowledge bank records the sector of a past job.'
    ]
  },
  tenderTypes: {
    title: 'Tender types',
    summary: 'The forms a bid can take. The Tender type field on a pursuit uses the active names from this list.',
    use: [
      'New adds a type. The number is assigned on save when left blank.',
      'Search finds a type by number, code, or name.',
      'Use the group to separate open competition, invited bids, panel work, and private work.',
      'Mark a type inactive when it should no longer be offered on a new tender.'
    ],
    fields: [
      ['Type no.', 'The firm’s number, such as DCSL-TT-001.'],
      ['Code', 'The short code, such as GRFP or EOI.'],
      ['Name', 'The type stored on the tender, such as Government RFP or Expression of Interest.'],
      ['Group', 'Chosen from the active tender-type groups under Settings → Organisation, in the order defined there.'],
      ['Status', 'Only active types appear when a tender is captured.']
    ],
    links: [
      'The tender form’s Tender type field is a searchable list of these active names.',
      'A tender already saved with a type keeps that name if the master row is later marked inactive.',
      'Win / loss reporting can be read by type once those reports open.'
    ]
  },
  sources: {
    title: 'Sources',
    summary: 'Where opportunities are found: a portal, an authority site, or a direct invitation. The Source field on a tender uses the active names.',
    use: [
      'New adds a source. The number is assigned on save when left blank.',
      'Add a web address when the source is a portal the team opens to find notices.',
      'Search finds a source by number, code, or name.',
      'Mark a source inactive when the team no longer watches it.'
    ],
    fields: [
      ['Source no.', 'The firm’s number, such as DCSL-SRC-001.'],
      ['Code', 'The short code, such as MAHA or MAN.'],
      ['Name', 'The source stored on the tender, such as MahaTender or Manual.'],
      ['Group', 'Chosen from the active source groups under Settings → Organisation, in the order defined there.'],
      ['Status', 'Only active sources appear when a tender is captured.']
    ],
    links: [
      'The tender form’s Source field is a searchable list of these active names.',
      'A portal address on the source record is the place the team goes back to for that channel.',
      'The register itself does not show the source. Open the tender to see which channel it came from.'
    ]
  },
  settings: {
    title: 'Settings',
    summary: 'Organisation settings for currency and calendar, mail, the AI provider, and the lists used when a tender is captured.',
    use: [
      'Organisation sets the currency lists, time zone, date style, country, financial year, password length, upload size, activity-log retention, personnel types, the values accepted on master imports, and the limits for users, tenders, and document storage. Import-value lists can be reordered by drag and marked inactive so they stay in settings but drop out of form dropdowns. The same page sets the short name, legal name, colours, logo, tagline, login line, Word letterhead, and the note shown at the top of Help. Estimates already saved stay in hundredths and are not converted.',
      'Email stores the sender and the mail server. Test connection checks the server. Send test mail posts a short message to an inbox.',
      'AI chooses Google Gemini or OpenAI and the model. The API key is encrypted and shown only as XXXXXXXXXXXX. Document questions, technical drafts, eligibility notes, library wording, and document summaries use whichever provider is saved and enabled.',
      'Notifications chooses whether the organisation may send email notices.',
      'Tender stages, Priority, and Urgency each open their list. Dropdown master shows every list together.',
      'New adds a value. Leave the code blank and it is taken from the label.',
      'Only active values appear when a tender is created or edited. A tender that already uses a removed value keeps that value.',
      'AI is available to a workspace administrator. The saved key cannot be read back.'
    ],
    fields: [
      ['Label', 'The name shown in the list, such as Under negotiation.'],
      ['Code', 'The value stored on the tender. It stays stable if the label is renamed.'],
      ['Order', 'Where the value sits in the list. Lower numbers come first.'],
      ['Status', 'Inactive values stay on old tenders and drop out of the form.']
    ],
    links: [
      'Tender stages, priority, and urgency from Settings are the lists on the tender form.',
      'Email is the mailbox used when a test message or a notice is sent.',
      'AI is the provider used for document questions, technical drafts, eligibility notes, library wording, and document summaries. Gemini is used when Gemini is saved. OpenAI is used when OpenAI is saved.'
    ],
    ai: {
      helps: 'This is the switch for every AI screen. Saving Gemini uses Gemini. Saving OpenAI uses OpenAI. While Use AI is off, the other screens stay quiet.',
      flow: ['Choose the provider', 'Paste the key', 'Turn Use AI on', 'Test the connection', 'Open a tender or the library'],
      use: [
        'Open the AI card. Choose Google Gemini or OpenAI and the model.',
        'Paste the key and save. Afterwards it is shown only as XXXXXXXXXXXX and cannot be read back.',
        'Turn Use AI on. Run the connection test. A pass means that provider accepted the key.',
        'The same setting covers Document Q&A, Summarise, Draft with AI, Suggest notes, and Suggest wording.'
      ],
      note: 'If the page says the server is not ready to store a key, the model can still be saved. Calls stay off until a key can be stored.'
    }
  },
  securities: {
    title: 'BG / EMD',
    summary: 'The register of EMD, bid security, and performance instruments. It records what the firm holds. It does not issue an instrument, take a payment, or post an accounting entry.',
    who: [
      'A bid manager or an administrator records an instrument and a later refund or release movement.',
      'The tender owner, or an administrator who did not record the payment, verifies the evidence. The person who recorded it cannot verify it.'
    ],
    use: [
      'Choose the tender by reference and title. The record stores that tender’s id.',
      'Save the type, the currency, the amount, the issuer, and the dates. A bank or issuer is a name from the master.',
      'Record a refund on a cash EMD as requested, received, forfeited, or adjusted to performance security. The destination is cash held as performance security, or a bank guarantee that already exists. Keep the original payment. A later movement is added to the history.',
      'Release a bank guarantee from the release action. That release is not a cash refund.',
      'Export Excel writes the instruments on this register.'
    ],
    fields: [
      ['Settlement', 'Cash, guarantee, or declaration, when someone has recorded it. A missing settlement on an EMD stays Unclassified. A performance security with no stored settlement is a guarantee, because that type is known.'],
      ['Cash outstanding', 'The refundable balance on a cash EMD, after receipts, forfeitures, and adjustments. Currencies stay separate. Cash held as performance security is not added here.'],
      ['Cash held', 'Cash retained as performance security. A transfer increases this total once. It does not increase guarantee exposure, and it does not rewrite the source movement.'],
      ['Guarantee exposure', 'The face of an open bank guarantee. A cash adjustment does not add itself on top of that face.'],
      ['Physical deadline', 'The dispatch date, kept apart from the online due date. The reminder lead is not the refund due date.']
    ],
    links: [
      'The same instruments appear on the tender’s BG / EMD tab, with the tender fee and the EMD requirement.',
      'Fee, EMD and BG on Reports uses these rows. Cash outstanding, cash held, and guarantee exposure are separate totals.',
      'Closing the tender keeps a release or refund follow-up. It does not delete the instrument.'
    ]
  },
  pursuit: {
    title: 'This tender',
    summary: 'The working page for one pursuit. The journey, the packages, the fee, and the submission are recorded here. AI drafts and answers. It does not decide the bid or approve the response.',
    who: [
      'The bid manager keeps the journey, the fee, the instruments, the role slots, and the response while the tender is open.',
      'The tender owner verifies a payment or an exemption they did not record, and records the portal submission after someone else has approved the pack.',
      'A person with bid-decision approval approves the pack. The person who prepares the pack cannot approve that version.',
      'A reviewer still approves or returns a technical section. Go / no-go is still a human decision.'
    ],
    use: [
      'Overview shows the journey and the four work packages. The package count does not approve submission. Costing can be in progress or blocked. Ready for review and Done stay closed while Final rate approval is pending. An approved delivery team does not close that gate.',
      'Eligibility & Go / No-Go keeps the calculated score, the recommendation, the review, and the authorised decision on separate lines. Confirm Solo or JV lead before a Go can move the tender to In preparation.',
      'Team / Resources records each required position as its own slot: the designation, qualification, experience, deployment, effort, and whether a CV, evidence, and consent are required. A blank stays blank. A resource user submits a version. Someone else approves or returns it. Editing an approved version opens a new draft and keeps the earlier approval as history. Final rate approval is not in this release.',
      'BG / EMD records the authority’s document fee and the EMD requirement, then the payment or the instrument. Unknown, not applicable, exempt, and zero stay distinct. A physical date and an online date are stored separately.',
      'Submission lists the gates that are still missing. Approve this submission pack is hidden for the owner who prepares the pack. Record portal submission needs a current approval by someone else, a portal reference, and an acknowledgement. Recording the acknowledgement does not approve the pack.',
      'Documents: upload the file, set classification to Standard, tick Eligible, then Extract text and Summarise. Document Q&A, Draft with AI, and Suggest notes are on this page and are described under How AI helps here.'
    ],
    fields: [
      ['Journey', 'Identified, Eligibility, Bid decision, In preparation, Ready to submit, Submitted, Under evaluation, and Result declared. Not eligible, No-bid, Won, Lost, Not qualified, Cancelled, and Withdrawn are outcomes. Each closed outcome needs an applicable reason.'],
      ['Work packages', 'Preparation, HR / Resources, Finance, and Costing. Dates are calendar days. Assignment required stays visible until an owner is named. Marking Finance done does not satisfy the fee and EMD check.'],
      ['Tender fee and EMD', 'The authority’s document fee and the EMD. This is not the firm’s expected consultancy fee. Details required means an amount, currency, due date, or mode is still missing. No amount is invented.'],
      ['Verification', 'Uploading a file does not verify it. A changed payment or proof clears the earlier verification.'],
      ['Cash and guarantee', 'Cash outstanding is the refundable balance. Cash held is performance security kept as cash. Guarantee exposure is an open bank guarantee. They are not added together.'],
      ['Eligible', 'Only a Standard file can be ticked. Unclassified, commercial confidential, and restricted files stay out of AI.'],
      ['Draft with AI', 'Shown while the section can still be edited. A submitted section hides it until the section is returned.'],
      ['Suggest notes', 'Needs the requirement text first. It does not set Go or No-Go.']
    ],
    links: [
      'An authorised Go creates one finance case and the four work packages. Opening the page again does not create a second set. No-bid creates neither.',
      'A refundable cash EMD keeps the original payment. Received, forfeited, and adjusted cannot pass that balance. A guarantee is released or invoked. It is not given a cash refund.',
      'Closing the tender cancels open package notices. Expiry, claim, and release follow-up stay open.',
      'A later change to the estimate, the technical version, the documents, the fee, the EMD, or a payment makes the pack approval stale. The earlier snapshot stays as it was.',
      'A generated submission pack, project finance, CV ranking, and technical or QCBS scoring are listed as not in this release. Delivery-team approval is recorded on Team / Resources.',
      'Approved reusable paragraphs come from the knowledge bank. The AI provider is the one saved under Settings, then AI. Commercials, timesheets, and BG / EMD do not call the provider.'
    ],
    ai: {
      helps: 'AI shortens the first draft and answers from files you have already marked eligible. The firm still owns the saved text.',
      flow: ['Switch AI on', 'Mark the file Eligible', 'Extract text', 'Ask, summarise, or draft', 'Save what you will stand behind'],
      use: [
        'If a screen says AI is not configured, open Settings and turn the provider on before trying again.',
        'Read every suggestion. Save a section or a requirement only after you have checked it.',
        'A submitted technical section is not redrafted until a reviewer returns it.'
      ]
    }
  }
};
